import React, { useMemo, useState } from 'react';
import { ChevronDown, CircleDashed, Crosshair, Gauge, KeyRound, PanelLeftClose, Unplug, UserRoundX } from 'lucide-react';
import { IconButton } from '../../../components/ui';
import { NetworkAnalysis } from '../graphAnalytics';
import { BriefPanel } from '../components/BriefPanel';
import type { NetworkNode } from '../types';
import { NetworkView, Vulnerabilities, modularityLabel, topPercent } from './networkModel';
import { EntityAvatar, EntityRow, MiniStat, MiniTabs, PercentileBar, RailSection, RoleGlyph } from './ui';

type ActorTab = 'influence' | 'brokerage' | 'degree' | 'gatekeepers';

export interface EntityCallbacks {
  onEntity: (id: string) => void;
  onEntityHover: (id: string | null) => void;
  entityColor: (id: string) => string | undefined;
  onReferences: (ids: string[]) => void;
}

interface BriefingRailProps {
  view: NetworkView;
  tieCount: number;
  analysis: NetworkAnalysis;
  names: Map<number, string>;
  vulns: Vulnerabilities;
  selectedId: string | null;
  lineOf: (n: NetworkNode) => string;
  onSelect: (id: string) => void;
  onHover: (id: string | null) => void;
  onHoverCluster: (community: number | null) => void;
  onFocusCluster: (community: number) => void;
  onShowTie: (a: string, b: string) => void;
  buildContext: () => string;
  ai: EntityCallbacks;
  onCollapse?: () => void;
  className?: string;
}

const CLUSTERING_HINT: Record<ReturnType<typeof modularityLabel>, string> = {
  Strong: 'Tight groups with few ties between them',
  Clear: 'Distinct groups with some cross-ties',
  Weak: 'Groups blur into each other',
  None: 'No meaningful grouping',
};

export const BriefingRail: React.FC<BriefingRailProps> = ({
  view,
  tieCount,
  analysis,
  names,
  vulns,
  selectedId,
  lineOf,
  onSelect,
  onHover,
  onHoverCluster,
  onFocusCluster,
  onShowTie,
  buildContext,
  ai,
  onCollapse,
  className = '',
}) => {
  const [tab, setTab] = useState<ActorTab>('influence');
  const [expanded, setExpanded] = useState(false);
  const [openVuln, setOpenVuln] = useState<string | null>(null);
  const node = (id: string) => view.nodesById.get(id);
  const people = view.nodes.filter((n) => n.type === 'stakeholder').length;
  const clustering = modularityLabel(analysis.modularity);

  const actorIds = useMemo(() => {
    if (tab === 'gatekeepers') return vulns.gatekeepers.map((g) => g.id);
    return analysis.ranked[tab];
  }, [tab, analysis, vulns]);
  const shown = actorIds.slice(0, expanded ? 15 : 6);

  // Cluster pairs without any direct tie: blind spots between groups
  const disconnectedPairs = useMemo(() => {
    const top = analysis.communities.slice(0, 6);
    if (top.length < 2) return [];
    const g = analysis.graph;
    const out: Array<[number, number]> = [];
    for (let i = 0; i < top.length; i++) {
      for (let j = i + 1; j < top.length; j++) {
        const other = new Set(top[j].members);
        const tied = top[i].members.some((id) => g.nbrs[g.index.get(id)!].some((k) => other.has(g.ids[k])));
        if (!tied) out.push([top[i].id, top[j].id]);
      }
    }
    return out;
  }, [analysis]);

  const vulnGroups = [
    {
      key: 'gatekeepers',
      icon: KeyRound,
      tone: 'text-amber-600',
      label: 'Single points of failure',
      hint: 'Removing them splits the network',
      ids: vulns.gatekeepers.map((g) => g.id),
      sub: (id: string) => {
        const cut = vulns.gatekeepers.find((g) => g.id === id)?.cutOff || 0;
        return `Cuts off ${cut} ${cut === 1 ? 'entity' : 'entities'}`;
      },
    },
    {
      key: 'isolates',
      icon: CircleDashed,
      tone: 'text-neutral-400',
      label: 'Isolated',
      hint: 'No ties in this view',
      ids: vulns.isolates,
      sub: (id: string) => lineOf(node(id)!),
    },
    {
      key: 'roles',
      icon: UserRoundX,
      tone: 'text-neutral-400',
      label: 'No current role',
      hint: 'People without a current position',
      ids: vulns.withoutRole,
      sub: (id: string) => lineOf(node(id)!),
    },
    {
      key: 'unscored',
      icon: Gauge,
      tone: 'text-neutral-400',
      label: 'Unscored influence',
      hint: 'No influence assessment yet',
      ids: vulns.unscored,
      sub: (id: string) => lineOf(node(id)!),
    },
  ].filter((g) => g.ids.length);

  return (
    <div className={`min-h-0 ${className}`}>
      {onCollapse && (
        <div className="flex h-11 items-center justify-between pl-4 pr-2">
          <span className="text-[13px] text-neutral-500">Briefing</span>
          <IconButton label="Hide briefing" size="sm" onClick={onCollapse}>
            <PanelLeftClose className="h-4 w-4" strokeWidth={1.75} />
          </IconButton>
        </div>
      )}

      {/* Headline numbers */}
      <div className="px-4 pb-3.5">
        <div className="grid grid-cols-3 divide-x divide-neutral-200/70 overflow-hidden rounded-2xl border border-neutral-200">
          <MiniStat label="Entities" value={view.nodes.length.toLocaleString()} hint={`${people} people · ${view.nodes.length - people} organizations`} />
          <MiniStat label="Ties" value={tieCount.toLocaleString()} />
          <MiniStat label="Clusters" value={analysis.communities.length} />
          <MiniStat
            label="Clustering"
            value={<span className="text-[15px]">{clustering}</span>}
            hint={`${CLUSTERING_HINT[clustering]} (modularity ${analysis.modularity.toFixed(2)})`}
            className="border-t border-neutral-200/70"
          />
          <MiniStat
            label="Density"
            value={`${(analysis.density * 100).toFixed(analysis.density < 0.1 ? 1 : 0)}%`}
            hint="Share of possible ties that exist"
            className="border-t border-neutral-200/70"
          />
          <MiniStat
            label="Connected"
            value={`${Math.round(analysis.largestComponentShare * 100)}%`}
            hint={`Largest connected group · ${analysis.components} ${analysis.components === 1 ? 'group' : 'groups'}`}
            className="border-t border-neutral-200/70"
          />
        </div>
      </div>

      {/* Key actors */}
      <RailSection title="Key actors">
        <MiniTabs<ActorTab>
          label="Rank key actors by"
          value={tab}
          onChange={(t) => {
            setTab(t);
            setExpanded(false);
          }}
          className="mb-2"
          items={[
            { value: 'influence', label: 'Influence', title: 'Tied to well-connected actors (PageRank)' },
            { value: 'brokerage', label: 'Brokers', title: 'Sit between groups (betweenness)' },
            { value: 'degree', label: 'Connectors', title: 'Most direct ties' },
            { value: 'gatekeepers', label: 'Gatekeepers', title: 'Single points of failure' },
          ]}
        />
        {shown.length === 0 ? (
          <p className="px-2 py-3 text-[12.5px] text-neutral-400">{tab === 'gatekeepers' ? 'No single points of failure' : 'No ties in view'}</p>
        ) : (
          <ol className="-mx-2">
            {shown.map((id, i) => {
              const n = node(id);
              const m = analysis.metrics.get(id);
              if (!n || !m) return null;
              const pct = tab === 'influence' ? m.pct.influence : tab === 'brokerage' ? m.pct.brokerage : m.pct.degree;
              const cut = vulns.gatekeepers.find((g) => g.id === id)?.cutOff;
              return (
                <li key={id} className="animate-fade-up motion-reduce:animate-none" style={{ animationDelay: `${i * 25}ms` }}>
                  <EntityRow
                    node={n}
                    active={id === selectedId}
                    onClick={() => onSelect(id)}
                    onHover={(h) => onHover(h ? id : null)}
                    lead={<span className="w-3 flex-shrink-0 text-right text-[11px] tabular-nums text-neutral-400">{i + 1}</span>}
                    sub={lineOf(n)}
                    below={
                      tab === 'gatekeepers' ? (
                        <span className="mt-1 block text-[11.5px] text-amber-700">
                          Cuts off {cut} {cut === 1 ? 'entity' : 'entities'}
                        </span>
                      ) : (
                        <span className="mt-1.5 flex items-center gap-2">
                          <PercentileBar value={pct} />
                          <span className="w-[52px] flex-shrink-0 text-right text-[11px] tabular-nums text-neutral-400">{topPercent(pct)}</span>
                        </span>
                      )
                    }
                    trailing={m.role !== 'peripheral' ? <RoleGlyph role={m.role} className="self-start" /> : undefined}
                  />
                </li>
              );
            })}
          </ol>
        )}
        {actorIds.length > 6 && (
          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            className="mt-1 inline-flex h-7 items-center gap-1 rounded-full px-2 text-[12px] text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-ink"
          >
            {expanded ? 'Show less' : `Show ${Math.min(15, actorIds.length) - 6} more`}
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`} strokeWidth={1.75} />
          </button>
        )}
      </RailSection>

      {/* Clusters */}
      <RailSection
        title="Clusters"
        aside={analysis.communities.length > 0 && <span title={CLUSTERING_HINT[clustering]}>{clustering} clustering</span>}
      >
        {analysis.communities.length === 0 ? (
          <p className="py-1 text-[12.5px] text-neutral-400">No clusters in view</p>
        ) : (
          <ul className="-mx-2" onMouseLeave={() => onHoverCluster(null)}>
            {analysis.communities.slice(0, 8).map((c) => (
              <li key={c.id}>
                <div
                  className="group flex items-center gap-2 rounded-xl px-2 py-1.5 transition-colors hover:bg-neutral-50"
                  onMouseEnter={() => onHoverCluster(c.id)}
                >
                  <button
                    type="button"
                    className="min-w-0 flex-1 text-left focus-visible:outline-none"
                    onClick={() => onFocusCluster(c.id)}
                    onFocus={() => onHoverCluster(c.id)}
                    onBlur={() => onHoverCluster(null)}
                  >
                    <span className="flex items-baseline gap-2">
                      <span className="min-w-0 flex-1 truncate text-[13px] text-ink">{names.get(c.id)}</span>
                      <span className="flex-shrink-0 text-[11.5px] tabular-nums text-neutral-400">{c.members.length}</span>
                    </span>
                    <span className="mt-1 flex items-center gap-2">
                      <span className="flex -space-x-1.5">
                        {c.leaders.slice(0, 3).map((id) => (
                          <EntityAvatar key={id} node={node(id)} size="xs" className="!h-5 !w-5 !text-[9px] ring-2 ring-white" />
                        ))}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-[11.5px] text-neutral-500">
                        {c.leaders
                          .slice(0, 2)
                          .map((id) => node(id)?.label)
                          .join(', ')}
                      </span>
                      <span
                        className="flex-shrink-0 text-[11px] tabular-nums text-neutral-400"
                        title={`${Math.round(c.cohesion * 100)}% of possible internal ties exist · ${c.externalTies} ties outside`}
                      >
                        {Math.round(c.cohesion * 100)}%
                      </span>
                    </span>
                  </button>
                  <IconButton label={`Focus ${names.get(c.id)}`} size="xs" onClick={() => onFocusCluster(c.id)} className="opacity-60 group-hover:opacity-100">
                    <Crosshair className="h-3.5 w-3.5" strokeWidth={1.75} />
                  </IconButton>
                </div>
              </li>
            ))}
          </ul>
        )}
        {disconnectedPairs.length > 0 && (
          <div className="mt-2 rounded-xl bg-neutral-50 px-3 py-2 text-[12px] leading-snug text-neutral-600">
            <p className="text-neutral-500">No direct ties between</p>
            {disconnectedPairs.slice(0, 3).map(([a, b]) => (
              <p key={`${a}-${b}`} className="mt-0.5 truncate text-ink">
                {names.get(a)} <span className="text-neutral-400">↔</span> {names.get(b)}
              </p>
            ))}
            {disconnectedPairs.length > 3 && <p className="mt-0.5 text-neutral-400">+{disconnectedPairs.length - 3} more pairs</p>}
          </div>
        )}
      </RailSection>

      {/* Vulnerabilities */}
      <RailSection title="Vulnerabilities">
        {vulnGroups.length === 0 && vulns.bridges.length === 0 ? (
          <p className="py-1 text-[12.5px] text-neutral-400">No structural weak points</p>
        ) : (
          <ul className="-mx-2">
            {vulnGroups.map((group) => {
              const open = openVuln === group.key;
              const Icon = group.icon;
              return (
                <li key={group.key}>
                  <button
                    type="button"
                    aria-expanded={open}
                    onClick={() => setOpenVuln(open ? null : group.key)}
                    title={group.hint}
                    className="flex h-9 w-full items-center gap-2.5 rounded-xl px-2 text-left text-[13px] transition-colors hover:bg-neutral-50"
                  >
                    <Icon className={`h-4 w-4 flex-shrink-0 ${group.tone}`} strokeWidth={1.75} />
                    <span className="flex-1 text-ink">{group.label}</span>
                    <span className="text-[12px] tabular-nums text-neutral-500">{group.ids.length}</span>
                    <ChevronDown className={`h-3.5 w-3.5 text-neutral-300 transition-transform ${open ? 'rotate-180' : ''}`} strokeWidth={1.75} />
                  </button>
                  {open && (
                    <ul className="mb-1 ml-6 animate-fade-up motion-reduce:animate-none">
                      {group.ids.slice(0, 10).map((id) => (
                        <li key={id}>
                          <EntityRow node={node(id)} sub={group.sub(id)} onClick={() => onSelect(id)} onHover={(h) => onHover(h ? id : null)} active={id === selectedId} />
                        </li>
                      ))}
                      {group.ids.length > 10 && <li className="px-2 py-1 text-[12px] text-neutral-400">+{group.ids.length - 10} more</li>}
                    </ul>
                  )}
                </li>
              );
            })}
            {vulns.bridges.length > 0 && (
              <li>
                <button
                  type="button"
                  aria-expanded={openVuln === 'bridges'}
                  onClick={() => setOpenVuln(openVuln === 'bridges' ? null : 'bridges')}
                  title="Ties whose loss splits the network"
                  className="flex h-9 w-full items-center gap-2.5 rounded-xl px-2 text-left text-[13px] transition-colors hover:bg-neutral-50"
                >
                  <Unplug className="h-4 w-4 flex-shrink-0 text-neutral-400" strokeWidth={1.75} />
                  <span className="flex-1 text-ink">Fragile ties</span>
                  <span className="text-[12px] tabular-nums text-neutral-500">{vulns.bridges.length}</span>
                  <ChevronDown className={`h-3.5 w-3.5 text-neutral-300 transition-transform ${openVuln === 'bridges' ? 'rotate-180' : ''}`} strokeWidth={1.75} />
                </button>
                {openVuln === 'bridges' && (
                  <ul className="mb-1 ml-6 animate-fade-up motion-reduce:animate-none">
                    {vulns.bridges.slice(0, 10).map((b) => (
                      <li key={`${b.a}-${b.b}`}>
                        <button
                          type="button"
                          onClick={() => onShowTie(b.a, b.b)}
                          onMouseEnter={() => onHover(b.a)}
                          onMouseLeave={() => onHover(null)}
                          className="flex w-full min-w-0 items-center gap-2 rounded-xl px-2 py-1.5 text-left transition-colors hover:bg-neutral-50"
                        >
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-[13px] text-ink">
                              {node(b.a)?.label} <span className="text-neutral-400">—</span> {node(b.b)?.label}
                            </span>
                            <span className="block text-[11.5px] tabular-nums text-neutral-500">
                              Splits {b.sizeA} / {b.sizeB}
                            </span>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            )}
          </ul>
        )}
      </RailSection>

      {/* AI assessment */}
      <RailSection title="Assessment">
        <BriefPanel
          domain="network"
          buildContext={buildContext}
          scopeLabel="Whole view"
          scopeKey={`${view.nodes.length}:${tieCount}`}
          onEntity={ai.onEntity}
          onEntityHover={ai.onEntityHover}
          entityColor={ai.entityColor}
        />
      </RailSection>
    </div>
  );
};
